import { ContractEndActionEnum, ContractStatusEnum } from '@monetize/types/app';
import { formatCurrency } from '@monetize/utils/core';
import { createColumnHelper } from '../../../components/Monetize/DataTableNew/appTable';
import { MdInfo } from 'react-icons/md';
import { ContractRenewalIndicator } from '../../../components/Contracts/ContractRenewalIndicator';
import {
  MFlex,
  MIcon,
  MTag,
  MText,
  MTooltip,
} from '../../../components/Monetize';
import { actionCatalogColumn } from '../../../components/Monetize/DataTableNew/columnCatalog/structuralColumns';
import {
  ColumnCatalog,
  ColumnContextBase,
} from '../../../components/Monetize/DataTableNew/columnCatalog/types';
import {
  dateBodyTemplate,
  dateTimeBodyTemplate,
  enumDisplayBodyTemplate,
  getColHeaderFilters,
  getColHeaderWithSize,
  getColumnFilterValuesForEnum,
  idWithExtraBodyTemplate,
  nameWithIdBodyTemplate,
} from '../../../components/Monetize/DataTableNew/tableUtilsNew';
import { CONTRACTS } from '../../../constants';
import { getBillGroupPageRoute } from '../../../constants/routes';
import { IContractWithBillGroup } from '../../../hooks/AccountDetail';

const columnHelper = createColumnHelper<IContractWithBillGroup>();

export interface ContractListAccountColumnContext extends ColumnContextBase<IContractWithBillGroup> {
  defaultCurrency?: string;
}

/** tableId: FilterStateKeys.CONTRACT_LIST */
export const CONTRACT_LIST_ACCOUNT_CATALOG: ColumnCatalog<
  IContractWithBillGroup,
  ContractListAccountColumnContext
> = [
  {
    id: 'id',
    label: 'ID',
    customizable: false,
    build: () =>
      columnHelper.accessor('id', {
        ...getColHeaderWithSize('ID', 200),
        cell: idWithExtraBodyTemplate<IContractWithBillGroup, string>({
          children: (info) => (
            <ContractRenewalIndicator contract={info.row.original} />
          ),
        }),
        enableSorting: false,
      }),
  },
  {
    id: 'billGroupId',
    label: 'Bill Group',
    build: () =>
      columnHelper.display({
        id: 'billGroupId',
        ...getColHeaderWithSize('Bill Group', 200),
        cell: nameWithIdBodyTemplate<IContractWithBillGroup, unknown>({
          idProp: 'billGroup.id' as keyof IContractWithBillGroup,
          nameProp: 'billGroup.name' as keyof IContractWithBillGroup,
          idLinkFn: (id) => getBillGroupPageRoute(id),
        }),
        enableSorting: false,
      }),
  },
  {
    id: 'startDate',
    label: 'Start Date',
    build: () =>
      columnHelper.accessor('startDate', {
        ...getColHeaderWithSize('Start Date', 150),
        ...getColHeaderFilters('DATE'),
        cell: dateBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'endDate',
    label: 'End Date',
    build: () =>
      columnHelper.accessor('endDate', {
        ...getColHeaderWithSize('End Date', 150),
        ...getColHeaderFilters('DATE'),
        cell: dateBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'endAction',
    label: 'Renewal Action',
    build: () =>
      columnHelper.accessor('endAction', {
        ...getColHeaderWithSize('Renewal Action', 180),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: getColumnFilterValuesForEnum(
            ContractEndActionEnum,
            CONTRACTS.CONTRACT_END_ACTION_UI_DISPLAY,
          ),
        }),
        cell: enumDisplayBodyTemplate({
          displayMap: CONTRACTS.CONTRACT_END_ACTION_UI_DISPLAY,
        }),
        enableSorting: true,
      }),
  },
  {
    id: 'status',
    label: 'Status',
    build: () =>
      columnHelper.accessor('status', {
        ...getColHeaderWithSize('Status', 150),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: getColumnFilterValuesForEnum(
            ContractStatusEnum,
            CONTRACTS.CONTRACT_STATUS_DISPLAY,
          ),
        }),
        cell: (info) => {
          const data = info.row.original;
          if (data.status === ContractStatusEnum.ACTIVE) {
            return <MTag variant="green">Active</MTag>;
          } else if (data.status === ContractStatusEnum.PENDING) {
            return (
              <MTooltip label={CONTRACTS.CONTRACT_PENDING_STATUS_TOOLTIP}>
                <MFlex alignItems="center">
                  <MText
                    lineClamp={1}
                    maxW="fit-content"
                    h="18px"
                    mr={1}
                    lineHeight="18px"
                  >
                    Pending
                  </MText>
                  <MIcon as={MdInfo} />
                </MFlex>
              </MTooltip>
            );
          }
          return enumDisplayBodyTemplate<
            IContractWithBillGroup,
            ContractStatusEnum
          >({
            displayMap: CONTRACTS.CONTRACT_STATUS_DISPLAY,
          })(info);
        },
        enableSorting: true,
      }),
  },
  {
    id: 'totalValue',
    label: 'Amount',
    build: ({ defaultCurrency }) =>
      columnHelper.accessor('totalValue', {
        ...getColHeaderWithSize('Amount', 150),
        ...getColHeaderFilters('NUMBER'),
        cell: (info) => {
          const data = info.row.original;
          return (
            <MTooltip label={data.totalValue} placement="bottom-end">
              <MText truncate lineClamp={1}>
                {formatCurrency(data.totalValue, {
                  currency: data.currency ?? defaultCurrency,
                })}
              </MText>
            </MTooltip>
          );
        },
        enableSorting: true,
      }),
  },
  {
    id: 'modifyDate',
    label: 'Updated',
    build: () =>
      columnHelper.accessor('modifyDate', {
        ...getColHeaderWithSize('Updated', 170),
        ...getColHeaderFilters('DATE'),
        cell: dateTimeBodyTemplate(),
        enableSorting: true,
      }),
  },
  actionCatalogColumn<IContractWithBillGroup>(),
];
